import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useSidecarStore } from '../../../store/useSidecarStore';
import {
  shouldCloseSidecarOnOutsideClick,
  shouldPortalSidecarToPip,
  shouldRenderMessagingSidecar,
  shouldShowSidecarSplitLayout,
} from '../../../store/sidecarBehavior';
import {
  closeSidecarPipWindow,
  getSidecarPipWindow,
  SIDECAR_PIP_WIDTH,
} from '../../../store/sidecarPip';
import { SidecarFloatingButton } from './SidecarFloatingButton';
import { SidecarPanelList } from './SidecarPanelList';
import { SidecarPanelChannel } from './SidecarPanelChannel';
import { NovaWorkspace } from '../../assistant/NovaWorkspace';
import { NOVA_DM_ID } from '../../assistant/novaTurn';
import { getAiAgentSettings } from '../../../services/cabinet/aiAgentSettings';
import { useNovaAgentStore } from '../../../store/useNovaAgentStore';
import { useLayoutStore } from '../../../store/useLayoutStore';
import { cn } from '../../../lib/cn';

export function MessagingSidecar() {
  const location = useLocation();
  const isOpen = useSidecarStore((s) => s.isOpen);
  const activeChannelId = useSidecarStore((s) => s.activeChannelId);
  const pinned = useSidecarStore((s) => s.pinned);
  const pipActive = useSidecarStore((s) => s.pipActive);
  const listMode = useSidecarStore((s) => s.listMode);
  const close = useSidecarStore((s) => s.close);
  const backToList = useSidecarStore((s) => s.backToList);
  const creation = useNovaAgentStore((s) => s.creation);
  const agent = useQuery({
    queryKey: ['cabinet', 'ai-agent'],
    queryFn: getAiAgentSettings,
    staleTime: 60_000,
  });
  const agentEnabled = agent.data?.enabled === true;
  const sidebarCollapsed = useLayoutStore((s) => s.sidebarCollapsed);
  const panelRef = useRef<HTMLDivElement>(null);
  const pipBody = pipActive ? getSidecarPipWindow()?.document.body ?? null : null;
  const inPip = shouldPortalSidecarToPip(pipActive, pipBody);
  const [pipWidth, setPipWidth] = useState(SIDECAR_PIP_WIDTH);

  useEffect(() => {
    if (!pipActive) closeSidecarPipWindow();
  }, [pipActive]);

  useEffect(() => {
    if (!pipActive) {
      setPipWidth(SIDECAR_PIP_WIDTH);
      return;
    }
    const pip = getSidecarPipWindow();
    if (!pip) return;
    const sync = () => setPipWidth(pip.innerWidth);
    sync();
    pip.addEventListener('resize', sync);
    return () => pip.removeEventListener('resize', sync);
  }, [pipActive, pipBody]);

  // Click-outside : réduit la sidecar sauf si elle est épinglée ou en PiP.
  // Les surfaces rendues en portail hors du panneau (modales Radix, mais aussi
  // le GifPicker qui fait createPortal vers document.body) portent
  // data-radix-portal / role="dialog" / data-cime-modal — on les ignore, sinon
  // un clic dessus serait vu comme "extérieur" et fermerait la messagerie.
  useEffect(() => {
    if (!isOpen || pipActive) return;
    function onMouseDown(e: MouseEvent) {
      const state = useSidecarStore.getState();
      if (!shouldCloseSidecarOnOutsideClick(state.pinned, state.pipActive)) return;
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      // Ignore les éléments rendus dans un portail Radix (modales, dropdowns, tooltips)
      const el = target instanceof Element ? target : (target.parentElement ?? null);
      if (el?.closest('[data-radix-portal], [role="dialog"], [data-cime-modal]')) return;
      close();
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isOpen, pipActive, close]);

  if (!shouldRenderMessagingSidecar(location.pathname)) {
    return null;
  }

  if (!isOpen && !pipActive) {
    return <SidecarFloatingButton />;
  }

  const novaOpen = activeChannelId === NOVA_DM_ID && agentEnabled;
  const novaCreation = novaOpen && creation && !inPip;
  const split = shouldShowSidecarSplitLayout(inPip, pipWidth, listMode);

  const panel = (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Messagerie"
      className={cn(
        'flex flex-col bg-white overflow-hidden',
        inPip
          ? 'w-full h-full'
          : novaCreation
            ? cn(
                'hidden md:flex fixed z-40 top-3 bottom-3 right-3 rounded-xl shadow-2xl border border-neutral-200',
                sidebarCollapsed ? 'left-[88px]' : 'left-[286px]',
              )
            : cn(
                'hidden md:flex fixed bottom-4 right-4 z-40 h-[720px] max-w-[calc(100vw-2rem)] rounded-xl shadow-2xl',
                split ? 'w-[880px]' : 'w-[620px]',
                pinned ? 'border-2 border-cime-primary' : 'border border-neutral-200',
              ),
      )}
    >
      {split ? (
        <div className="flex flex-1 min-h-0">
          <aside className="w-[240px] shrink-0 border-r border-neutral-200 flex flex-col min-h-0 min-w-0">
            <SidecarPanelList selectedChannelId={activeChannelId} />
          </aside>
          <section className="flex-1 min-w-0 min-h-0 flex flex-col">
            {novaOpen ? (
              <NovaWorkspace hideBack />
            ) : activeChannelId ? (
              <SidecarPanelChannel channelId={activeChannelId} hideBack />
            ) : (
              <div className="flex-1 flex items-center justify-center px-6 text-sm text-cime-text-muted text-center">
                Choisissez un salon ou un message direct
              </div>
            )}
          </section>
        </div>
      ) : novaOpen ? (
        <NovaWorkspace onBack={backToList} />
      ) : activeChannelId ? (
        <SidecarPanelChannel channelId={activeChannelId} />
      ) : (
        <SidecarPanelList />
      )}
    </div>
  );

  if (inPip && pipBody) {
    return createPortal(panel, pipBody);
  }

  return panel;
}
